import {
  Box,
  Button,
  Fade, Paper, Step,
  StepLabel, Stepper, Typography,
} from '@mui/material';
import React from 'react';
import * as Sentry from '@sentry/react';
import { QontoConnector } from '../../QontoStepIcon/components';
import QontoStepIcon from '../../QontoStepIcon';
import useStyles from './styles';
import getPartnerButton from '../../PartnerButtons';
import { Partner } from '../types';
import ErrorFallback, { errorHandler } from '../../ErrorFallback';

type LeadFormProps ={
  title: string,
  stepperSteps: number;
  partner: Partner,
  submitButtonProps:{
    disabledSteps: number[],
    getButtonLabel: (step: number, partnerName: string) => string,
    testingLabel: 'preloan-submit-button' | 'authorization-submit-button',
  },
  backButtonProps: {
    getButtonLabel: (step: number, partnerName: string) => string,
    disabledSteps: number[],
  },
  getStepContent: (params: {
    activeStep: number,
    handleNext: () => void,
    handleStep: (step: number) => void,
    handleEnableLoading: (state: boolean) => void,
  }) => React.ReactElement<any, any>,
  customBackSteps?: {activeStep: number, customBackStep: number}[],
  onSubmit: () => void,
  onErrorReset: () => void,
  hideStepperTitle?: boolean,
  hideStepperSteps?: boolean,
  minimalLogo?: string,
  plainSteps?: number[],
  onActiveStepChange?: (step: number) => void,
}
const defaultProps = {
  customBackSteps: [],
  hideStepperTitle: false,
  hideStepperSteps: false,
  minimalLogo: undefined,
  plainSteps: [] as number[],
  onActiveStepChange: undefined,
};

export default function LeadFormRedesign(props: LeadFormProps) {
  const {
    title,
    stepperSteps,
    partner,
    submitButtonProps,
    backButtonProps,
    getStepContent,
    customBackSteps = [],
    onSubmit,
    onErrorReset,
    hideStepperTitle,
    hideStepperSteps,
    minimalLogo,
    plainSteps = [],
    onActiveStepChange,
  } = props;

  const classes = useStyles();
  const [activeStep, setActiveStep] = React.useState(0);

  const handleNext = () => setActiveStep((prevStep) => prevStep + 1);
  const handleBack = () => setActiveStep((prevStep) => prevStep - 1);

  const handleStep = (step: number) => setActiveStep(step);

  const [enableLoading, setEnableLoading] = React.useState(false);
  const isPlain = plainSteps.includes(activeStep);

  React.useEffect(() => {
    onActiveStepChange?.(activeStep);
  }, [activeStep]);

  const handleEnableLoading = (state: boolean) => setEnableLoading(state);

  const handleBackSteps = (step: number) => {
    if (customBackSteps.length > 0) {
      const customBackStep = customBackSteps.find((item) => item.activeStep === step);
      if (customBackStep) {
        return handleStep(customBackStep.customBackStep);
      }
    }
    return handleBack();
  };
  return (
    <Sentry.ErrorBoundary
      fallback={<ErrorFallback />}
      onError={
          (error, componentStack) => {
            errorHandler(
              error as Error,
              { componentStack: componentStack as string },
              undefined,
              undefined,
            );
          }
      }
      onReset={onErrorReset}
    >
      <Fade
        in
        {...({ timeout: 1000 })}
      >
        <div style={isPlain ? { width: '100%', height: '100%' } : { width: '100%' }}>
          {!hideStepperTitle && !isPlain && (
          <>
            <br />
            <Typography
              className={classes.title}
              fontWeight="bold"
              variant="h6"
              component="div"
              align="center"
              gutterBottom
            >
              {title}
            </Typography>
          </>
          )}
          {!hideStepperSteps && (
          <Stepper
            className={classes.stepper}
            alternativeLabel
            activeStep={activeStep}
            connector={<QontoConnector />}
          >
            {[...Array(stepperSteps).keys()].map((num) => (
              <Step key={num}>
                <StepLabel StepIconComponent={QontoStepIcon} />
              </Step>
            ))}
          </Stepper>
          )}
          <Paper
            className={isPlain ? undefined : classes.paper}
            elevation={0}
            style={isPlain ? {
              background: 'transparent',
              boxShadow: 'none',
              width: '100%',
              minHeight: '100%',
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              textAlign: 'left',
            } : { borderRadius: '10px', marginInline: 'auto' }}
          >
            {!isPlain && (
            <div style={{
              borderRadius: '10px 10px 0px 0px',
              paddingTop: 5,
            }}
            >
              <div className={classes.isotipo}>
                <object
                  data={minimalLogo}
                  aria-labelledby="Isotipo"
                  height="25px"
                />
              </div>
            </div>
            )}
            <Box
              sx={isPlain
                ? {
                  flex: 1,
                  display: 'flex',
                  flexDirection: 'column',
                  minHeight: 0,
                }
                : { padding: { sx: '2.5% 5px', md: '2.5% 25px' } }}
            >
              {getStepContent({
                activeStep,
                handleNext,
                handleStep,
                handleEnableLoading,
              })}
            </Box>
          </Paper>
          {!isPlain && getPartnerButton({
            partner,
            className: classes.submitButton,
            testingLabel: submitButtonProps.testingLabel,
            step: activeStep,
            buttonLabel: submitButtonProps.getButtonLabel(activeStep, partner.partnerName),
            onClick: onSubmit,
            disabled: (enableLoading
              || submitButtonProps.disabledSteps.includes(activeStep)
              || submitButtonProps.getButtonLabel(activeStep, partner.partnerName) === 'Ir a Portal Inmobiliario'),
          })}
          {!isPlain && (
          <div>
            <Button
              className={classes.backButton}
              disabled={backButtonProps.disabledSteps.includes(activeStep) || enableLoading}
              color="primary"
              onClick={() => handleBackSteps(activeStep)}
            >
              Volver
            </Button>
          </div>
          )}
        </div>
      </Fade>
    </Sentry.ErrorBoundary>
  );
}

LeadFormRedesign.defaultProps = defaultProps;
